import type {Thread} from '@github-ui/diff-lines/conversation-types'
import {useMemo} from 'react'

import {
  buildIssueCommentsAuthorList,
  buildThreadPreviewsAuthorList,
  sortThreadPreviewsByDiffOrder,
} from '../../helpers/comments-panel-helper'
import type {IssueComment} from '../../page-data/payloads/issue-comments'
import {useDiffPathOrderMap, useIssueComments, useReviewSummaries, useThreads} from '../../stores/selectors'
import type {AuthorFilterItem} from './CommentsFilter'
import {
  buildConversationItems,
  reviewSummaryToIssueComment,
  type ConversationItem,
} from './utils/review-summary-to-issue-comment'

export interface CommentsData {
  /** Thread previews sorted by their position in the diff. */
  threadPreviews: Thread[]
  /** The unfiltered number of diff comments, used for "showing X of Y" messaging. */
  totalDiffCommentCount: number
  /** Issue comments and review summaries sorted chronologically, preserving their original types. */
  conversationItems: ConversationItem[]
  /** Issue comments and review summaries flattened to a single shape, for filtering. */
  mergedConversationComments: IssueComment[]
  /** Unique authors of the thread previews, for the author filter. */
  threadPreviewsAuthorList: AuthorFilterItem[]
  /** Unique authors of the conversation comments, for the author filter. */
  issueCommentsAuthorList: AuthorFilterItem[]
}

/**
 * Derives the comment collections rendered by `Comments` and its view controls from the
 * pull request stores.
 *
 * This is UI-surface agnostic — it can be used anywhere comments are rendered, not just
 * in the docked side panel.
 */
export function useCommentsData(): CommentsData {
  const unsortedThreadPreviews = useThreads()
  const diffPathOrderMap = useDiffPathOrderMap()
  const issueComments = useIssueComments()
  const reviewSummaries = useReviewSummaries()

  const threadPreviews = useMemo(
    () => sortThreadPreviewsByDiffOrder(unsortedThreadPreviews, diffPathOrderMap),
    [unsortedThreadPreviews, diffPathOrderMap],
  )

  // Build a chronologically sorted list of conversation items (issue comments + review summaries)
  // preserving original types so each renders with its dedicated component
  const conversationItems = useMemo(
    () => buildConversationItems(issueComments, reviewSummaries),
    [issueComments, reviewSummaries],
  )

  // Flatten to IssueComment shape for filtering (useFilterIssueCommentsIds expects IssueComment[])
  const mergedConversationComments = useMemo(() => {
    const convertedSummaries = reviewSummaries.map(reviewSummaryToIssueComment)
    return [...issueComments, ...convertedSummaries]
  }, [issueComments, reviewSummaries])

  const threadPreviewsAuthorList = useMemo(() => buildThreadPreviewsAuthorList(threadPreviews), [threadPreviews])

  const issueCommentsAuthorList = useMemo(
    () => buildIssueCommentsAuthorList(mergedConversationComments),
    [mergedConversationComments],
  )

  return {
    threadPreviews,
    totalDiffCommentCount: unsortedThreadPreviews.length,
    conversationItems,
    mergedConversationComments,
    threadPreviewsAuthorList,
    issueCommentsAuthorList,
  }
}
